import { useState } from "react";
import { ChevronDown, Laptop, Smartphone } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { saveMyMobile } from "../../../lib/calls";
import { formatPhone } from "../../../lib/phone";
import { Pop, useClosePops } from "../../shell/Pop";
import { mobileAvailable, type DialCapabilities, type DialFlags } from "../dialFlags";

/**
 * Where the OPERATOR takes this call: in this browser, or on their own phone.
 *
 * The gap this closes is the plainest one in the module. Everything else about
 * a call has been choosable for months - the line, the recording, the language
 * it announces in - and the one thing that was fixed is the thing that decides
 * whether an operator can work at all: their leg has always been a browser tab,
 * so leaving the desk meant leaving the phone system. This control makes the
 * first participant a telephone instead, and the room, the customer's leg, the
 * recording and the announcement carry on exactly as they were.
 *
 * A `Pop` rather than a fourth toggle pill beside the other three, for two
 * reasons that both point the same way. It is not a property of the CALL, so
 * sitting it in the row of things-that-happen-to-the-call would read as one;
 * and it is also the only place the number can be typed, because Settings ->
 * Telephony is behind `manage:crm.calls` and the person whose mobile this is
 * usually does not hold that key.
 *
 * ⚠ NOT DRAWN AT ALL when the account bridges calls with `<Dial><Number>`. That
 * bridge is one document with two legs and has nowhere for a telephone to join,
 * so the server would refuse - and a permanently dead control is furniture the
 * operator has to learn to ignore. See `DialCapabilities.conference`.
 */
export function MobilePick({
  wanted,
  effective,
  caps,
  setWanted,
  onNumberSaved,
}: {
  wanted: DialFlags;
  /** The clamped set, so the pill lights only when the call really goes to the phone. */
  effective: DialFlags;
  caps: DialCapabilities;
  setWanted: (next: DialFlags) => void;
  /**
   * A number was saved or cleared, so the capabilities this pad was opened with
   * are out of date.
   *
   * Handed upwards rather than re-fetched here: `caps.mobileNumber` is what
   * `effectiveDialFlags` clamps against, so a save that only updated this
   * component would leave the choice switched on and clamped straight back off
   * - a control that visibly refuses its own click.
   */
  onNumberSaved: (mobileNumber: string) => void;
}) {
  const t = useT();
  const closePops = useClosePops();
  const saved = caps.mobileNumber.trim();
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = (value: string) => {
    setBusy(true);
    setErr(null);
    saveMyMobile(value)
      .then((res) => {
        setDraft(res.mobileNumber);
        onNumberSaved(res.mobileNumber);
        // Clearing the number has to take the choice with it, or the pad would
        // sit on "my mobile" with nothing to ring. The clamp would catch it, but
        // the pill would go on saying the call is going to a phone.
        if (!res.mobileNumber) setWanted({ ...wanted, viaMobile: false });
      })
      .catch((e: unknown) => {
        // The server's own code, kept: "that is not a phone number" and "the
        // request never landed" are different mistakes, and one of them is the
        // operator's to fix.
        setErr(e instanceof ApiError && e.message === "invalid_number" ? "bad" : "failed");
      })
      .finally(() => setBusy(false));
  };

  return (
    <Pop
      trigger={(open) => (
        <button
          type="button"
          className={cn("crm-dtog", (open || effective.viaMobile) && "is-on")}
          aria-haspopup="listbox"
          aria-expanded={open}
          title={t("calls.dial.mobileHint")}
        >
          {effective.viaMobile ? (
            <Smartphone className="size-3.5" />
          ) : (
            <Laptop className="size-3.5" />
          )}
          {effective.viaMobile ? t("calls.dial.mobileOn") : t("calls.dial.mobileOff")}
          <ChevronDown className={cn("size-3.5 transition-transform", open && "rotate-180")} />
        </button>
      )}
    >
      <button
        type="button"
        className={cn("crm-mi", !wanted.viaMobile && "is-on")}
        onClick={() => {
          setWanted({ ...wanted, viaMobile: false });
          closePops();
        }}
      >
        <Laptop className="size-3.5" />
        {t("calls.dial.mobileOff")}
      </button>
      {/* Offered only once there is a number to ring. Before that the field
          below is the whole point of the menu, and an option that cannot be
          taken would be the second control in one panel that does nothing. */}
      {mobileAvailable(caps) ? (
        <button
          type="button"
          className={cn("crm-mi", wanted.viaMobile && "is-on")}
          onClick={() => {
            setWanted({ ...wanted, viaMobile: true });
            closePops();
          }}
        >
          <Smartphone className="size-3.5" />
          {formatPhone(saved)}
        </button>
      ) : null}

      <div className="crm-dmob">
        <label htmlFor="crm-dmob-num">{t("calls.dial.mobileNumber")}</label>
        <div className="crm-dmob-row">
          <div className="crm-inp">
            <input
              id="crm-dmob-num"
              type="tel"
              value={draft}
              placeholder="+372 5xxx xxxx"
              disabled={busy}
              onChange={(e) => {
                setDraft(e.target.value);
                setErr(null);
              }}
              // Enter saves, because the panel has no form around it and the
              // nearest one is the dial pad's own - which would place a call.
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                save(draft);
              }}
            />
          </div>
          <button
            type="button"
            className="crm-btn crm-btn-sm"
            disabled={busy}
            onClick={() => save(draft)}
          >
            {busy ? t("calls.dial.mobileSaving") : t("calls.dial.mobileSave")}
          </button>
        </div>
        {saved ? (
          <button
            type="button"
            className="crm-dmob-clear"
            disabled={busy}
            onClick={() => {
              setDraft("");
              save("");
            }}
          >
            {t("calls.dial.mobileClear")}
          </button>
        ) : null}
        <p className={cn("crm-dmob-note", err && "is-bad")}>
          {err === "bad"
            ? t("calls.dial.mobileBad")
            : err
              ? t("calls.dial.mobileSaveFailed")
              : t("calls.dial.mobileExplain")}
        </p>
      </div>
    </Pop>
  );
}
